// src/data/adminUsersClient.js
//
// Data client for role-mappings admin endpoints.
//
// Endpoints:
//   GET /api/people?signedIn=1    verified signed-in People subset
//   GET /api/people    assignable People directory
//   GET /api/admin/role-mappings    list all mappings
//   PUT /api/admin/role-mappings    replace all mappings

import { AuthError, NetworkError, ValidationError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";

async function jsonRequest(url, init) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    });
  } catch (err) {
    log.error("adminUsersClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("adminUsersClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  return res.json();
}

// ─── Role mappings ────────────────────────────────────────────────────────────

export async function getRoleMappings() {
  return jsonRequest("/api/admin/role-mappings", { method: "GET" });
}

/**
 * @param {Array<{ okta_group: string, app_role: string }>} mappings
 */
export async function putRoleMappings(mappings) {
  return jsonRequest("/api/admin/role-mappings", {
    method: "PUT",
    body: JSON.stringify(mappings),
  });
}

// The Directory and optional account-holder filter share the people roster.
// Activity endpoints still take a linked
// users UUID, never the returned person's UUID.
export async function getSignedInUsers() {
  return jsonRequest("/api/people?signedIn=1", { method: "GET" });
}

export async function getProvisionedUsers() {
  return jsonRequest("/api/people", { method: "GET" });
}

// Same Directory contract, gated by the immutable DEV controller.
export async function getDevActAsPeople() {
  return jsonRequest("/api/dev-act-as/people", { method: "GET" });
}

export async function importDirectory(csv, mode) {
  return jsonRequest("/api/people-directory/import", {
    method: "POST",
    body: JSON.stringify({ csv, mode }),
  });
}

// ─── Sensitivity taxonomy ────────────────────────────────────────────────────

export async function getSensitivityTaxonomy() {
  return jsonRequest("/api/admin/sensitivity-taxonomy", { method: "GET" });
}

/**
 * @param {Record<string, "general" | "financial_sensitive">} areas
 */
export async function putSensitivityTaxonomy(areas) {
  return jsonRequest("/api/admin/sensitivity-taxonomy", {
    method: "PATCH",
    body: JSON.stringify({ areas }),
  });
}

export { AuthError, NetworkError, ValidationError };
